import { ReactComponent as Checkmark } from "assets/images/check-green.svg";
import { ReactComponent as Clock } from "assets/images/clock-icon.svg";
import { ReactComponent as MoveToInbox } from "assets/images/move-to-inbox.svg";
import { ReactComponent as Pin } from "assets/images/pin-icon.svg";
import useLanguage from "hooks/language";
import Appointment from "models/appointment";
import { getBookingFlowConfirmationMedicalTests } from "models/chosen-medical-test";
import { getLocaleName } from "models/medical-test";
import React from "react";
import { Link } from "react-router-dom";
import routes from "utils/routes";
import {
    getClinicAddress,
    getClinicDirectionsDetails,
    getClinicDoorCodeDisplay,
    getClinicMapsHref,
    hasClinicDoorCode,
} from "utils/clinic-helpers";
import {
    getBookingFlowConfirmationDateTime,
    getEstimatedResultsDateForAppointment,
    ServiceType,
} from "utils/private-service/date";
import styles from "./booking-flow-confirmation-body.module.css";

const MOBILITY_EMAIL = "info@prelib.com";

function CheckListItem({
    children,
}: {
    children: React.ReactNode;
}): JSX.Element {
    return (
        <li className={styles.checkListItem}>
            <Checkmark className={styles.checkListIcon} aria-hidden />
            <div className={styles.checkListContent}>{children}</div>
        </li>
    );
}

export type BookingFlowConfirmationBodyProps = {
    appointment: Appointment;
    subtitle: string;
    remindersTitle: string;
    reminderItems: React.ReactNode;
    googleCalendarLink?: string;
    iCalendarUrl?: string;
    icsFileName: string;
    /** STI screening confirmation shows estimated lab results date. */
    showEstimatedResults?: boolean;
};

export default function BookingFlowConfirmationBody({
    appointment,
    subtitle,
    remindersTitle,
    reminderItems,
    googleCalendarLink,
    iCalendarUrl,
    icsFileName,
    showEstimatedResults = false,
}: BookingFlowConfirmationBodyProps): JSX.Element {
    const { language, translations } = useLanguage();
    const t = translations.bookingFlowConfirmation;
    const privateLabLabel =
        translations.privateServiceSection.confirmation.privateLab;
    const clinic = appointment.clinic;

    const formattedDate = getBookingFlowConfirmationDateTime(
        appointment,
        language
    );
    const tests = getBookingFlowConfirmationMedicalTests(
        appointment.chosenMedicalTests,
        language
    );
    const showEntranceCode = hasClinicDoorCode(clinic);
    const directionsDetails = getClinicDirectionsDetails(clinic, language);
    const clinicImage = clinic.clinicImage?.trim();
    const showHowToFindUs = !!(directionsDetails || clinicImage);

    const serviceType = appointment.usesPrivateLab
        ? ServiceType.private
        : ServiceType.public;
    const labTypeSuffix = appointment.usesPrivateLab
        ? ` ${privateLabLabel}`
        : "";
    const estimatedResultsValue = showEstimatedResults
        ? `${getEstimatedResultsDateForAppointment(
              appointment,
              serviceType,
              language
          )}${labTypeSuffix}`
        : null;

    const handleAddToCalendar = (): void => {
        if (googleCalendarLink) {
            window.open(googleCalendarLink, "_blank", "noopener,noreferrer");
            return;
        }
        if (iCalendarUrl) {
            const link = document.createElement("a");
            link.href = iCalendarUrl;
            link.download = icsFileName;
            link.click();
        }
    };

    const showAddToCalendar = !!(googleCalendarLink ?? iCalendarUrl);

    return (
        <>
            <header className={styles.header}>
                <Checkmark className={styles.headerIcon} aria-hidden />
                <h1 className={styles.headerTitle}>{t.title}</h1>
                <p className={styles.headerSubtitle}>{subtitle}</p>
            </header>

            <div className={styles.dateTime}>
                <Clock className={styles.rowIcon} aria-hidden />
                <p className={styles.dateTimeValue}>{formattedDate}</p>
            </div>

            <div className={styles.clinic}>
                <Pin className={styles.rowIcon} aria-hidden />
                <div className={styles.clinicName}>{clinic.name}</div>
                <a
                    className={styles.clinicAddressLink}
                    href={getClinicMapsHref(clinic)}
                    target="_blank"
                    rel="noreferrer"
                >
                    {getClinicAddress(clinic)}
                </a>
            </div>

            {estimatedResultsValue && (
                <div
                    className={styles.dateTime}
                    aria-label={`${t.results}: ${estimatedResultsValue}`}
                >
                    <MoveToInbox className={styles.rowIcon} aria-hidden />
                    <p className={styles.dateTimeValue}>
                        {estimatedResultsValue}
                    </p>
                </div>
            )}

            {showEntranceCode && (
                <div className={styles.entranceCode}>
                    <div className={styles.entrancePrompt}>
                        {t.entranceCodePrompt}
                    </div>
                    <div className={styles.entranceCodeValue}>
                        {getClinicDoorCodeDisplay(clinic)}
                    </div>
                </div>
            )}

            {tests.length > 0 && (
                <section
                    className={styles.testsSection}
                    aria-labelledby="tests-today-heading"
                >
                    <h2
                        id="tests-today-heading"
                        className={styles.testsHeading}
                    >
                        {t.testsToday}
                    </h2>
                    <ul className={styles.checkList}>
                        {tests.map((test) => (
                            <li key={test.id} className={styles.checkListItem}>
                                <Checkmark
                                    className={styles.checkListIcon}
                                    aria-hidden
                                />
                                <span className={styles.checkListContent}>
                                    {getLocaleName(test.medicalTest, language)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <section
                className={styles.remindersSection}
                aria-labelledby="confirmation-reminders"
            >
                <h2 className={styles.sectionTitle} id="confirmation-reminders">
                    {remindersTitle}
                </h2>
                <ul className={styles.checkList}>{reminderItems}</ul>
            </section>

            {showHowToFindUs && (
                <section
                    className={styles.howToFindUsSection}
                    aria-labelledby="how-to-find-us"
                >
                    <h2 className={styles.sectionTitle} id="how-to-find-us">
                        {t.howToFindUs}
                    </h2>
                    {directionsDetails && (
                        <div className={styles.directions}>
                            <span aria-hidden="true">⚠️ </span>
                            <strong className={styles.directionsCareful}>
                                {t.beCareful}
                            </strong>{" "}
                            {directionsDetails}
                        </div>
                    )}
                    {clinicImage && (
                        <img
                            className={styles.clinicPhoto}
                            src={clinicImage}
                            alt={t.clinicPhotoAlt}
                        />
                    )}
                </section>
            )}

            <p className={styles.mobilityNote}>
                {t.reducedMobilityBeforeEmail}
                <a
                    className={styles.mobilityLink}
                    href={`mailto:${MOBILITY_EMAIL}`}
                >
                    {MOBILITY_EMAIL}
                </a>
                {t.reducedMobilityAfterEmail}
            </p>

            <div className={styles.actionsFooter}>
                {showAddToCalendar && (
                    <button
                        type="button"
                        className={styles.calendarButton}
                        onClick={handleAddToCalendar}
                    >
                        {t.addToCalendar}
                    </button>
                )}
                <Link className={styles.homeLink} to={routes.HOME}>
                    {t.backToHomepage}
                </Link>
            </div>
        </>
    );
}

export { CheckListItem as BookingFlowConfirmationCheckListItem };
